/**
 * CustomMenus — user-defined sidebar folders/submenus that organize saved
 * searches and leads. Supports create/rename/delete (with confirm), icon
 * picker, 1-level submenus and HTML5 drag-and-drop of items between menus.
 */
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Folder, FolderOpen, Plus, ChevronDown, ChevronRight, MoreVertical,
  Pencil, Trash2, FolderPlus, X, Check, Search, User, Link as LinkIcon,
  Building2, Landmark, Hammer, Store, Truck, Wrench, Home, Star, Flame,
  MapPin, Briefcase, Loader2,
} from "lucide-react";
import { useLang } from "../i18n/LangContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const MENU_ICONS = {
  folder: Folder, building: Building2, landmark: Landmark, hammer: Hammer,
  store: Store, truck: Truck, wrench: Wrench, home: Home, star: Star,
  flame: Flame, mappin: MapPin, briefcase: Briefcase,
};

const ITEM_ICONS = { search: Search, lead: User, link: LinkIcon };

function IconPicker({ value, onPick }) {
  return (
    <div className="grid grid-cols-6 gap-1 p-1.5 bg-black/30 rounded-lg" data-testid="cm-icon-picker">
      {Object.entries(MENU_ICONS).map(([key, Icon]) => (
        <button key={key} type="button" onClick={() => onPick(key)}
          data-testid={`cm-icon-${key}`}
          className={`h-7 w-7 rounded flex items-center justify-center transition ${
            value === key ? "bg-[#C9A24E] text-[#1A1A2E]" : "text-white/60 hover:bg-white/10 hover:text-white"}`}>
          <Icon className="w-3.5 h-3.5" />
        </button>
      ))}
    </div>
  );
}

export default function CustomMenus() {
  const navigate = useNavigate();
  const { lang } = useLang();
  const isPt = lang === "pt";
  const [menus, setMenus] = useState([]);
  const [openIds, setOpenIds] = useState({});
  const [creating, setCreating] = useState(null); // {parent_id} | null
  const [editing, setEditing] = useState(null);   // menu being renamed
  const [name, setName] = useState("");
  const [icon, setIcon] = useState("folder");
  const [menuKebab, setMenuKebab] = useState(null); // menu id with kebab open
  const [dragOver, setDragOver] = useState(null);
  const [addingLead, setAddingLead] = useState(null); // menu id
  const [leadQuery, setLeadQuery] = useState("");
  const [leadOptions, setLeadOptions] = useState([]);
  const [leadLoading, setLeadLoading] = useState(false);
  const debounceRef = useRef(null);

  const load = () => {
    axios.get(`${API}/custom-menus`)
      .then(({ data }) => setMenus(data.menus || []))
      .catch(() => {});
  };
  useEffect(() => { load(); }, []);
  useEffect(() => {
    const h = () => load();
    window.addEventListener("custom-menus-changed", h);
    const openH = (e) => { if (e.detail) setOpenIds((o) => ({ ...o, [e.detail]: true })); };
    window.addEventListener("custom-menus-open", openH);
    return () => {
      window.removeEventListener("custom-menus-changed", h);
      window.removeEventListener("custom-menus-open", openH);
    };
  }, []);

  const roots = menus.filter((m) => !m.parent_id);
  const childrenOf = (id) => menus.filter((m) => m.parent_id === id);

  const submitCreate = async () => {
    const n = name.trim();
    if (!n) return;
    try {
      await axios.post(`${API}/custom-menus`, { name: n, icon, parent_id: creating?.parent_id || null });
      toast.success(isPt ? `Menu "${n}" criado` : `Menu "${n}" created`);
      setCreating(null); setName(""); setIcon("folder");
      if (creating?.parent_id) setOpenIds((o) => ({ ...o, [creating.parent_id]: true }));
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erro"); }
  };

  const submitRename = async () => {
    const n = name.trim();
    if (!n || !editing) return;
    try {
      await axios.patch(`${API}/custom-menus/${editing.id}`, { name: n, icon });
      setEditing(null); setName(""); setIcon("folder");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erro"); }
  };

  const deleteMenu = async (m) => {
    const kids = childrenOf(m.id).length;
    const nItems = (m.items || []).length;
    const msg = isPt
      ? `Excluir o menu "${m.name}"${kids ? ` e ${kids} submenu(s)` : ""}${nItems ? ` com ${nItems} item(ns)` : ""}? Esta ação não pode ser desfeita.`
      : `Delete menu "${m.name}"${kids ? ` and ${kids} submenu(s)` : ""}? This cannot be undone.`;
    if (!window.confirm(msg)) return;
    try {
      await axios.delete(`${API}/custom-menus/${m.id}`);
      toast.success(isPt ? "Menu excluído" : "Menu deleted");
      setMenuKebab(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erro"); }
  };

  const removeItem = async (menuId, itemId) => {
    try {
      await axios.delete(`${API}/custom-menus/${menuId}/items/${itemId}`);
      load();
    } catch (_e) {}
  };

  const onDropItem = async (targetMenuId, e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(null);
    // Menu-on-menu drop → reorder (same parent) or nest (root onto root's child area)
    const menuData = e.dataTransfer.getData("application/x-al-menu");
    if (menuData) {
      try {
        const { menu_id } = JSON.parse(menuData);
        if (!menu_id || menu_id === targetMenuId) return;
        const dragged = menus.find((m) => m.id === menu_id);
        const target = menus.find((m) => m.id === targetMenuId);
        if (!dragged || !target) return;
        if ((dragged.parent_id || null) === (target.parent_id || null)) {
          // Same level → reorder: insert dragged before target
          const siblings = menus.filter((m) => (m.parent_id || null) === (target.parent_id || null))
            .sort((a, b) => (a.order || 0) - (b.order || 0))
            .map((m) => m.id)
            .filter((id) => id !== menu_id);
          const idx = siblings.indexOf(targetMenuId);
          siblings.splice(idx < 0 ? siblings.length : idx, 0, menu_id);
          await axios.post(`${API}/custom-menus/reorder`, { ordered_ids: siblings });
          toast.success(isPt ? "Menus reordenados" : "Menus reordered");
        } else if (!dragged.parent_id && !target.parent_id) {
          return;
        } else {
          // Different levels → nest dragged under target root (1 level max)
          if (target.parent_id) return;
          await axios.patch(`${API}/custom-menus/${menu_id}`, { parent_id: targetMenuId });
          toast.success(isPt ? "Menu movido para dentro" : "Menu nested");
        }
        load();
      } catch (_e) {
        toast.error(isPt ? "Falha ao reordenar" : "Failed to reorder");
      }
      return;
    }
    try {
      const data = JSON.parse(e.dataTransfer.getData("application/x-al-menu-item") || "{}");
      if (!data.item_id || data.from_menu === targetMenuId) return;
      await axios.post(`${API}/custom-menus/${data.from_menu}/items/${data.item_id}/move`,
        { to_menu_id: targetMenuId });
      toast.success(isPt ? "Item movido" : "Item moved");
      load();
    } catch (_e) {
      toast.error(isPt ? "Falha ao mover item" : "Failed to move item");
    }
  };

  const searchLeads = (q) => {
    setLeadQuery(q);
    clearTimeout(debounceRef.current);
    if (!q.trim()) { setLeadOptions([]); return; }
    debounceRef.current = setTimeout(async () => {
      setLeadLoading(true);
      try {
        const { data } = await axios.get(`${API}/saved-leads`);
        const all = data.leads || data.items || data || [];
        setLeadOptions(
          (Array.isArray(all) ? all : [])
            .filter((l) => (l.name || "").toLowerCase().includes(q.toLowerCase()))
            .slice(0, 6));
      } catch (_e) { setLeadOptions([]); }
      finally { setLeadLoading(false); }
    }, 350);
  };

  const addLeadItem = async (menuId, lead) => {
    try {
      await axios.post(`${API}/custom-menus/${menuId}/items`, {
        type: "lead", label: lead.name,
        route: `/kanban?q=${encodeURIComponent(lead.name)}`, lead_id: lead.id,
      });
      toast.success(isPt ? `"${lead.name}" adicionado` : `"${lead.name}" added`);
      setAddingLead(null); setLeadQuery(""); setLeadOptions([]);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erro"); }
  };

  const inlineForm = (submit, cancel) => (
    <div className="px-2 py-2 bg-white/[0.04] rounded-lg space-y-1.5 mt-1" data-testid="cm-inline-form">
      <input autoFocus value={name} onChange={(e) => setName(e.target.value)}
        onKeyDown={(e) => { if (e.key === "Enter") submit(); if (e.key === "Escape") cancel(); }}
        placeholder={isPt ? "Nome do menu..." : "Menu name..."}
        data-testid="cm-name-input"
        className="w-full h-7 px-2 rounded bg-black/30 border border-white/15 text-[11.5px] text-white outline-none focus:border-[#C9A24E]" />
      <IconPicker value={icon} onPick={setIcon} />
      <div className="flex gap-1">
        <button type="button" onClick={submit} data-testid="cm-form-save"
          className="flex-1 h-6 rounded bg-[#C9A24E] text-[#1A1A2E] text-[10.5px] font-bold inline-flex items-center justify-center gap-1">
          <Check className="w-3 h-3" /> {isPt ? "Salvar" : "Save"}
        </button>
        <button type="button" onClick={cancel}
          className="h-6 px-2 rounded bg-white/10 text-white/70 text-[10.5px] inline-flex items-center justify-center">
          <X className="w-3 h-3" />
        </button>
      </div>
    </div>
  );

  const renderItems = (m) => (m.items || []).map((it) => {
    const ItIcon = ITEM_ICONS[it.type] || LinkIcon;
    return (
      <div key={it.id} draggable
        onDragStart={(e) => {
          e.stopPropagation();
          e.dataTransfer.effectAllowed = "move";
          e.dataTransfer.setData("application/x-al-menu-item",
            JSON.stringify({ item_id: it.id, from_menu: m.id }));
        }}
        data-testid={`cm-item-${it.id}`}
        className="group/item flex items-center gap-1.5 px-2 py-1.5 rounded text-[11.5px] text-white/60 hover:bg-white/5 hover:text-white cursor-grab active:cursor-grabbing">
        <ItIcon className="w-3 h-3 shrink-0 opacity-70" />
        <button type="button" onClick={() => navigate(it.route)}
          className="flex-1 text-left truncate" title={it.label}>
          {it.label}
        </button>
        <button type="button" onClick={() => removeItem(m.id, it.id)}
          data-testid={`cm-item-remove-${it.id}`}
          className="opacity-0 group-hover/item:opacity-100 text-white/40 hover:text-rose-400">
          <X className="w-3 h-3" />
        </button>
      </div>
    );
  });

  const renderMenu = (m, depth = 0) => {
    const Icon = MENU_ICONS[m.icon] || Folder;
    const OpenIcon = openIds[m.id] ? FolderOpen : Icon;
    const kids = childrenOf(m.id);
    const isOpen = !!openIds[m.id];
    return (
      <div key={m.id} className={depth ? "ml-3 border-l border-white/10 pl-1.5" : ""}>
        {editing?.id === m.id ? inlineForm(submitRename, () => { setEditing(null); setName(""); }) : (
          <div
            draggable
            onDragStart={(e) => {
              e.stopPropagation();
              e.dataTransfer.effectAllowed = "move";
              e.dataTransfer.setData("application/x-al-menu",
                JSON.stringify({ menu_id: m.id }));
            }}
            onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); setDragOver(m.id); }}
            onDragLeave={() => setDragOver((d) => (d === m.id ? null : d))}
            onDrop={(e) => onDropItem(m.id, e)}
            className={`group flex items-center gap-2 px-2 py-1.5 rounded-lg transition cursor-grab active:cursor-grabbing ${
              dragOver === m.id ? "bg-[#C9A24E]/20 ring-1 ring-[#C9A24E]" : "hover:bg-white/5"}`}
            data-testid={`cm-menu-${m.id}`}>
            <button type="button" className="flex items-center gap-2 flex-1 min-w-0 text-left"
              onClick={() => setOpenIds((o) => ({ ...o, [m.id]: !o[m.id] }))}>
              <OpenIcon className="w-[15px] h-[15px] shrink-0 text-[#C9A24E]" />
              <span className="flex-1 truncate text-[12.5px] text-white/80">{m.name}</span>
              {(m.items || []).length > 0 && (
                <span className="text-[9px] px-1 rounded bg-white/10 text-white/50 tabular-nums">{m.items.length}</span>
              )}
              {isOpen ? <ChevronDown className="w-3 h-3 opacity-50" /> : <ChevronRight className="w-3 h-3 opacity-50" />}
            </button>
            <div className="relative">
              <button type="button" data-testid={`cm-kebab-${m.id}`}
                onClick={() => setMenuKebab(menuKebab === m.id ? null : m.id)}
                className="opacity-0 group-hover:opacity-100 text-white/40 hover:text-white p-0.5">
                <MoreVertical className="w-3.5 h-3.5" />
              </button>
              {menuKebab === m.id && (
                <div className="absolute right-0 top-5 z-50 w-40 rounded-lg bg-[#232342] border border-white/15 shadow-xl py-1 text-[11.5px]"
                  data-testid={`cm-kebab-menu-${m.id}`}>
                  <button type="button" onClick={() => { setEditing(m); setName(m.name); setIcon(m.icon || "folder"); setMenuKebab(null); }}
                    data-testid={`cm-rename-${m.id}`}
                    className="w-full flex items-center gap-2 px-3 py-1.5 text-white/75 hover:bg-white/10">
                    <Pencil className="w-3 h-3" /> {isPt ? "Editar nome/ícone" : "Edit name/icon"}
                  </button>
                  {!m.parent_id && (
                    <button type="button" onClick={() => { setCreating({ parent_id: m.id }); setName(""); setIcon("folder"); setMenuKebab(null); }}
                      data-testid={`cm-add-sub-${m.id}`}
                      className="w-full flex items-center gap-2 px-3 py-1.5 text-white/75 hover:bg-white/10">
                      <FolderPlus className="w-3 h-3" /> {isPt ? "Criar submenu" : "Add submenu"}
                    </button>
                  )}
                  <button type="button" onClick={() => { setAddingLead(m.id); setMenuKebab(null); setOpenIds((o) => ({ ...o, [m.id]: true })); }}
                    data-testid={`cm-add-lead-${m.id}`}
                    className="w-full flex items-center gap-2 px-3 py-1.5 text-white/75 hover:bg-white/10">
                    <User className="w-3 h-3" /> {isPt ? "Adicionar lead" : "Add lead"}
                  </button>
                  <button type="button" onClick={() => deleteMenu(m)}
                    data-testid={`cm-delete-${m.id}`}
                    className="w-full flex items-center gap-2 px-3 py-1.5 text-rose-300 hover:bg-rose-500/10">
                    <Trash2 className="w-3 h-3" /> {isPt ? "Excluir" : "Delete"}
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
        {creating?.parent_id === m.id && inlineForm(submitCreate, () => setCreating(null))}
        {addingLead === m.id && (
          <div className="px-2 py-1.5 bg-white/[0.04] rounded-lg mt-1 space-y-1" data-testid="cm-lead-search">
            <input autoFocus value={leadQuery} onChange={(e) => searchLeads(e.target.value)}
              placeholder={isPt ? "Buscar lead salvo..." : "Search saved lead..."}
              data-testid="cm-lead-search-input"
              className="w-full h-7 px-2 rounded bg-black/30 border border-white/15 text-[11px] text-white outline-none focus:border-[#C9A24E]" />
            {leadLoading && <Loader2 className="w-3 h-3 animate-spin text-white/50 mx-auto" />}
            {leadOptions.map((l) => (
              <button key={l.id} type="button" onClick={() => addLeadItem(m.id, l)}
                data-testid={`cm-lead-option-${l.id}`}
                className="w-full text-left px-2 py-1 rounded text-[11px] text-white/70 hover:bg-white/10 truncate">
                {l.name}
              </button>
            ))}
            <button type="button" onClick={() => { setAddingLead(null); setLeadQuery(""); setLeadOptions([]); }}
              className="w-full h-5 text-[10px] text-white/40 hover:text-white/70">
              {isPt ? "cancelar" : "cancel"}
            </button>
          </div>
        )}
        {isOpen && (
          <div className="ml-4 mt-0.5 space-y-0.5">
            {renderItems(m)}
            {kids.map((k) => renderMenu(k, depth + 1))}
            {(m.items || []).length === 0 && kids.length === 0 && (
              <p className="px-2 py-1 text-[10px] text-white/30 italic">
                {isPt ? "Arraste itens aqui ou use ⋮" : "Drag items here or use ⋮"}
              </p>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="pt-2 mt-2 border-t border-white/5" data-testid="custom-menus">
      <div className="flex items-center justify-between px-2 mb-1">
        <span className="text-[10px] font-bold uppercase tracking-wider text-white/40">
          {isPt ? "Meus Menus" : "My Menus"}
        </span>
        <button type="button" data-testid="cm-create-root"
          onClick={() => { setCreating({ parent_id: null }); setName(""); setIcon("folder"); }}
          title={isPt ? "Criar novo menu" : "Create new menu"}
          className="w-5 h-5 rounded flex items-center justify-center text-[#C9A24E] hover:bg-[#C9A24E]/15">
          <Plus className="w-3.5 h-3.5" />
        </button>
      </div>
      {creating && !creating.parent_id && inlineForm(submitCreate, () => setCreating(null))}
      <div className="space-y-0.5">
        {roots.map((m) => renderMenu(m))}
        {roots.length === 0 && !creating && (
          <p className="px-2 py-1 text-[10.5px] text-white/30">
            {isPt ? "Organize buscas e leads em pastas — clique em +" : "Organize searches & leads into folders — click +"}
          </p>
        )}
      </div>
    </div>
  );
}
